/**
 * 运营总览 · 渠道客户用量（反向透视）— /ops/analytics/channel-usage
 * 行=客户（区间用量降序）· 列=日期 · 2026-07-15 老板需求·示意图已审。
 * 数据：GET /api/v1/analytics/channel-usage（渠道归组同库存日历渠道视图）。
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { getChannelUsagePivot } from "@/services/usage";
import type { ChannelGroup, UsageModule } from "@/services/types/usage";
import { CHANNEL_META } from "@/shared/ui/ChannelUsage";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { OperationsTabs } from "./OperationsTabs";

const pad = (n: number) => String(n).padStart(2, "0");
function nowYM(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}
function shiftYM(ym: string, delta: number): string {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}
function monthRange(ym: string): [string, string] {
  const [y, m] = ym.split("-").map(Number);
  return [`${ym}-01`, `${ym}-${pad(new Date(y, m, 0).getDate())}`];
}
function listDates(from: string, to: string): string[] {
  const out: string[] = [];
  let [y, m, d] = from.split("-").map(Number);
  let cur = from;
  while (cur <= to && out.length < 63) {
    out.push(cur);
    const nd = new Date(y, m - 1, d + 1);
    y = nd.getFullYear(); m = nd.getMonth() + 1; d = nd.getDate();
    cur = `${y}-${pad(m)}-${pad(d)}`;
  }
  return out;
}

const MODULES: Array<[UsageModule, string]> = [["attraction", "门票"], ["hotel", "酒店"], ["product", "产品"]];
const GROUPS: ChannelGroup[] = ["b2b", "ota", "direct"];
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export function ChannelUsagePivotPage() {
  const [module, setModule] = useState<UsageModule>("attraction");
  const [channelFilter, setChannelFilter] = useState<"all" | ChannelGroup>("all");
  const [ym, setYm] = useState(nowYM());
  const [customOn, setCustomOn] = useState(false);
  const [customStart, setCustomStart] = useState(monthRange(nowYM())[0]);
  const [customEnd, setCustomEnd] = useState(monthRange(nowYM())[1]);
  const [cellPop, setCellPop] = useState<{ customer: string; iso: string; orderIds: string[] } | null>(null);

  const [dateFrom, dateTo] = customOn ? [customStart, customEnd] : monthRange(ym);
  const rangeOk = dateFrom <= dateTo;

  const q = useQuery({
    queryKey: ["analytics", "channel-usage", module, dateFrom, dateTo],
    enabled: rangeOk,
    queryFn: () => getChannelUsagePivot(module, dateFrom, dateTo),
    retry: false,
  });
  const unit = q.data?.unit ?? "";
  const rows = useMemo(
    () => (q.data?.customers ?? []).filter((c) => channelFilter === "all" || c.channel_group === channelFilter),
    [q.data, channelFilter],
  );
  const dates = useMemo(() => (rangeOk ? listDates(dateFrom, dateTo) : []), [dateFrom, dateTo, rangeOk]);
  const today = todayISO();

  // 顶部渠道小计（随渠道筛选前的全量数据算·展示三组盘子）
  const groupTotals = useMemo(() => {
    const t: Record<ChannelGroup, number> = { b2b: 0, ota: 0, direct: 0 };
    for (const c of q.data?.customers ?? []) t[c.channel_group] += c.total_qty;
    return t;
  }, [q.data]);

  const chip = (active: boolean) =>
    `rounded-full px-3 py-1 text-xs font-medium transition-colors ${active ? "bg-blue-600 text-white" : "border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`;

  return (
    <PageShell title="👥 渠道客户用量" subtitle="行 = 客户（区间用量降序）· 列 = 日期 · 已确认订单口径 · 单位随模块（酒店=间夜 / 门票=张 / 产品=人）">
      <OperationsTabs />

      {/* 筛选条 */}
      <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <span className="text-xs text-slate-500">模块</span>
        {MODULES.map(([m, label]) => (
          <button key={m} onClick={() => setModule(m)} className={chip(module === m)}>{label}</button>
        ))}
        <span className="mx-1.5 h-4 w-px bg-slate-200" />
        <span className="text-xs text-slate-500">渠道</span>
        <button onClick={() => setChannelFilter("all")} className={chip(channelFilter === "all")}>全部</button>
        {GROUPS.map((g) => (
          <button key={g} onClick={() => setChannelFilter(g)} className={chip(channelFilter === g)}>
            {CHANNEL_META[g].label}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-1.5">
          {!customOn && (
            <>
              <button onClick={() => setYm((v) => shiftYM(v, -1))} className="rounded-md border border-slate-300 p-1 hover:bg-slate-50"><ChevronLeft size={14} /></button>
              <span className="min-w-[4.5rem] text-center text-xs font-medium text-slate-700">{ym}</span>
              <button onClick={() => setYm((v) => shiftYM(v, 1))} className="rounded-md border border-slate-300 p-1 hover:bg-slate-50"><ChevronRight size={14} /></button>
            </>
          )}
          {customOn && (
            <span className="flex items-center gap-1.5 text-xs text-slate-500">
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1 text-xs" />
              至
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1 text-xs" />
            </span>
          )}
          <button onClick={() => setCustomOn((v) => !v)} className={chip(customOn)}>自定义</button>
        </span>
      </div>

      {/* 渠道小计 */}
      <div className="flex flex-wrap gap-2 text-xs">
        {GROUPS.map((g) => (
          <span key={g} className={`inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 shadow-sm ring-1 ring-slate-200 ${CHANNEL_META[g].text}`}>
            <span className={`inline-block h-2.5 w-2.5 rounded-sm ${CHANNEL_META[g].bar}`} />
            {CHANNEL_META[g].label} 合计 <b>{groupTotals[g]}</b> {unit}
          </span>
        ))}
      </div>

      {/* 透视表 */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        {!rangeOk ? (
          <p className="py-8 text-center text-sm text-rose-600">起始日期不能晚于结束日期</p>
        ) : q.isLoading ? (
          <LoadingState />
        ) : q.isError ? (
          <button onClick={() => q.refetch()} className="w-full rounded-lg border border-rose-200 bg-rose-50 py-6 text-center text-sm font-medium text-rose-700 hover:bg-rose-100">
            加载失败（区间最长 62 天），点击重试
          </button>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">该区间此模块无已确认订单用量</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="border-separate border-spacing-0 text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 min-w-[13rem] border-b border-slate-200 bg-white px-2 py-1.5 text-left font-medium text-slate-500">客户</th>
                  <th className="min-w-[6rem] border-b border-slate-200 bg-white px-2 py-1.5 text-right font-medium text-slate-500">合计</th>
                  {dates.map((iso) => {
                    const d = new Date(iso + "T00:00:00");
                    const dow = d.getDay();
                    const isWeekend = dow === 0 || dow === 6;
                    return (
                      <th key={iso} className={`min-w-[3rem] border-b border-slate-200 px-1 py-1.5 text-center font-medium ${iso === today ? "text-blue-600" : isWeekend ? "bg-slate-50 text-slate-500" : "text-slate-500"}`}>
                        <div>{d.getMonth() + 1}/{d.getDate()}</div>
                        <div className="text-[10px] font-normal">{"日一二三四五六"[dow]}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const meta = CHANNEL_META[c.channel_group];
                  const nameEl = (
                    <>
                      <span className={`mr-1.5 inline-block h-2.5 w-2.5 rounded-sm align-[-1px] ${meta.bar}`} />
                      <span className="font-medium text-slate-800">{c.customer_name}</span>
                      {c.source && c.channel_group === "direct" && (
                        <span className="ml-1 text-[10px] text-slate-400">{c.source}</span>
                      )}
                    </>
                  );
                  return (
                    <tr key={`${c.channel_group}-${c.customer_id ?? c.customer_name}`}>
                      <td className="sticky left-0 z-10 whitespace-nowrap border-b border-slate-100 bg-white px-2 py-1.5">
                        {c.channel_group === "b2b" && c.customer_id ? (
                          <Link to={`/admin/channel-partners/${c.customer_id}`} className="hover:underline" title="打开同业客户详情">
                            {nameEl}
                          </Link>
                        ) : (
                          nameEl
                        )}
                      </td>
                      <td className="whitespace-nowrap border-b border-slate-100 px-2 py-1.5 text-right">
                        <b className="text-slate-800">{c.total_qty}</b> {unit} · <span className="text-blue-600">{c.order_count} 单</span>
                      </td>
                      {dates.map((iso) => {
                        const v = c.days[iso];
                        if (!v) return <td key={iso} className="border-b border-slate-100 px-1 py-1.5 text-center text-slate-300">—</td>;
                        return (
                          <td
                            key={iso}
                            onClick={() => setCellPop({ customer: c.customer_name, iso, orderIds: c.day_orders[iso] ?? [] })}
                            className={`cursor-pointer border-b border-slate-100 px-1 py-1.5 text-center font-semibold hover:ring-1 hover:ring-inset hover:ring-blue-400 ${meta.text} ${c.channel_group === "b2b" ? "bg-blue-50" : c.channel_group === "ota" ? "bg-violet-50" : "bg-emerald-50"}`}
                          >
                            {v}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* cell 下钻：该客户该日订单链接 */}
      {cellPop && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setCellPop(null)}>
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">{cellPop.iso} · {cellPop.customer}</h3>
              <button onClick={() => setCellPop(null)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X size={16} /></button>
            </div>
            {cellPop.orderIds.length === 0 ? (
              <p className="text-sm text-slate-400">无订单明细</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {cellPop.orderIds.map((oid) => (
                  <Link key={oid} to={`/orders/${oid}`} className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50">
                    订单 …{oid.slice(-6)}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </PageShell>
  );
}

export default ChannelUsagePivotPage;
